/**
 * Test and lab users (UX audit D06).
 *
 * The admin users directory is also the invite list, and the Synthetic
 * Customer Lab creates invitees every time a flow runs. On the dev preview the
 * e2e-admin persona therefore sees 360 users with only a handful active — the
 * demo users a bank reviewer is meant to look at are buried under lab noise.
 *
 * A user is lab/test-created when its email carries a marker the lab tooling
 * uses. This is presentation only: the directory list hides these rows by
 * default behind a user-visible toggle, and never deletes or alters them.
 *
 * Markers recognised (matched on the email, case-insensitively):
 *   - local part beginning `lab-`, `lab_`, `lab.`, `lab+` (e.g. `lab-approver@…`);
 *   - local part beginning `e2e-`, `e2e_`, `e2e.` (the e2e personas);
 *   - local part beginning `adm0`, `adm1`, … (the ADM-0x flow invitees);
 *   - a `+lab` / `+e2e` subaddress tag on the local part;
 *   - the lab/SSO fixture domains `alloybuildlab.com` and `agents.alloy.build`.
 *
 * No lab invitee email of this shape appears in this checkout's committed
 * fixtures yet (the fixtures carry human addresses such as
 * `ava@example.com` and `wallet.operator@northbridge.example`), so the rule is
 * deliberately narrow: it flags only addresses the lab tooling would mint, and
 * leaves every human-shaped address visible.
 */

const LAB_LOCAL_MARKER = /^(?:lab|e2e)[._+-]/i;
const LAB_FLOW_LOCAL = /^adm\d/i;
const LAB_LOCAL_TAG = /[+._](?:lab|e2e)(?:[+._-]|$)/i;
const LAB_DOMAINS = ["alloybuildlab.com", "agents.alloy.build"];

/** The fields of an `AppUser` that decide whether it is a lab/test user. */
export interface LabUserSource {
  email?: string | null;
  name?: string | null;
}

/**
 * True when a directory row was created by test/lab tooling rather than by a
 * person the operator invited. The admin users page hides these rows by
 * default (UX audit D06).
 */
export function isLabUser(user: LabUserSource): boolean {
  const email = (user.email ?? "").trim().toLowerCase();
  const at = email.lastIndexOf("@");
  if (at <= 0 || at === email.length - 1) return false;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);

  if (LAB_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`))) return true;
  if (LAB_LOCAL_MARKER.test(local)) return true;
  if (LAB_FLOW_LOCAL.test(local)) return true;
  if (LAB_LOCAL_TAG.test(local)) return true;
  return false;
}
